import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import getUserNotifications from "api/requests/getUserNotifications";
import postUserNotificationDismiss from "api/requests/postUserNotificationDismiss";
import { Action, Category } from "constants/events";
import { UserNotification } from "custom-types/User";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { localizeText } from "utils/localizeText";
import { trackEvent } from "utils/trackEvent";

import SectionHeader from "components/botanic/SectionHeader";
import InfoIcon from "components/Icons/info.svg";
import NotificationCard from "components/UserProfile/Updates/NotificationCard";
import UserProfileLayout from "components/UserProfile/UserProfileLayout";

const NOTIFICATIONS_PER_PAGE = 25;

const UpdatesPage: React.FC = () => {
  const countryCode = useDomainCountryCode();
  const router = useRouter();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalNotificationCount, setTotalNotificationCount] = useState(0);
  const [currentPage] = useState(
    router.query?.page ? Number(router.query.page) : 1,
  );

  useEffect(() => {
    getUserNotifications(NOTIFICATIONS_PER_PAGE, currentPage, countryCode).then(
      ({
        notifications,
        metadata,
      }: {
        notifications: UserNotification[];
        metadata: { total: number };
      }) => {
        setNotifications(notifications);
        setTotalNotificationCount(metadata.total);
        setIsLoading(false);
      },
    );
  }, [currentPage, router]);

  const loadMoreNotifications = () => {
    getUserNotifications(NOTIFICATIONS_PER_PAGE, currentPage, countryCode).then(
      ({
        notifications: newNotifications,
      }: {
        notifications: UserNotification[];
      }) => {
        setNotifications([...notifications, ...newNotifications]);
      },
    );
  };

  useEffect(() => {
    if (notifications.length === 0) {
      return;
    }

    postUserNotificationDismiss(countryCode).then(() => {
      window.dispatchEvent(new CustomEvent("notificationsRefresh"));
    });
  }, [notifications]);

  const noUpdatesCopyPlural = localizeText("dispensaries", countryCode);
  const noUpdatesCopySingular = localizeText("dispensary", countryCode);
  const noUpdatesCopySingularCap = localizeText("Dispensary", countryCode);

  return (
    <UserProfileLayout currentPage={`${noUpdatesCopySingularCap} updates`}>
      <div className="md:flex justify-between items-center mb-lg">
        <SectionHeader
          tidbit={
            totalNotificationCount && !isLoading
              ? `${notifications?.length} of ${totalNotificationCount} total`
              : ""
          }
        >
          {noUpdatesCopySingularCap} updates
        </SectionHeader>
        <Link
          href="/me/settings"
          className="flex items-center justify-between bg-green-20 rounded py-sm px-lg mt-sm md:mt-none mb-sm"
        >
          <div className="text-xs">Manage push notification settings</div>
          <InfoIcon className="ml-sm" height="16" width="16" />
        </Link>
      </div>
      {isLoading ? (
        <div className="text-center my-section">Loading...</div>
      ) : (
        <div className="mb-section">
          {notifications.length > 0 ? (
            <>
              <div className="mb-xl">
                {notifications.map((notification: UserNotification) => (
                  <div className="mb-md" key={notification.id}>
                    <NotificationCard
                      title={notification.title}
                      text={notification.content}
                      imageUrl={notification.imageUrl}
                      readAt={notification.readAt}
                      date={notification.createdAt}
                      href={notification.url}
                    />
                  </div>
                ))}
              </div>
              {totalNotificationCount > notifications.length && (
                <button
                  className="button px-xxl mx-auto"
                  onClick={loadMoreNotifications}
                >
                  load more
                </button>
              )}
            </>
          ) : (
            <>
              <p>
                Follow {noUpdatesCopyPlural} to see updates here. If you’ve
                already followed a {noUpdatesCopySingular}, stay tuned — any new
                updates will be posted here!
              </p>
              <Link
                className="button"
                href="/dispensaries"
                onClick={() => {
                  trackEvent(
                    Category.webProfile,
                    Action.click,
                    "notification empty state",
                  );
                }}
              >
                see {noUpdatesCopyPlural} near you
              </Link>
            </>
          )}
        </div>
      )}
    </UserProfileLayout>
  );
};

export const getServerSideProps = createGetServerSideProps();

export default UpdatesPage;
